import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { BillToModel, CarrierDto, ShipToModel, ShipViaDto } from "@insite/client-framework/Types/ApiModels";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import BillingAddressInfoDisplay, { BillingAddressInfoDisplayStyles } from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/BillingAddressInfoDisplay";
import ShippingAddressInfoDisplay, {
    ShippingAddressInfoDisplayStyles,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/ShippingAddressInfoDisplay";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { getIsUkWebsite } from "@tSurya/Common/Utilities/websiteUtils";
import EditIcon from "@tSurya/Icons/EditIcon";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    billTo: BillToModel;
    shipTo: ShipToModel;
    carrier: CarrierDto;
    shipVia: ShipViaDto;
    deliveryDate: Date | null;
    onEditBillTo: () => void;
    onEditShipTo: () => void;
    extendedStyles?: BillingAndShippingAddressStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;

    return {
        cart,
        isUkWebsite: getIsUkWebsite(state),
    };
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface BillingAndShippingAddressStyles {
    container?: GridContainerProps;
    carrierGridItem?: GridItemProps;
    carrierHeadingText?: TypographyPresentationProps;
    carrierText?: TypographyPresentationProps;
    shippingServiceGridItem?: GridItemProps;
    shippingServiceHeadingText?: TypographyPresentationProps;
    shippingServiceText?: TypographyPresentationProps;
    deliveryDateGridItem?: GridItemProps;
    deliveryDateHeadingText?: TypographyPresentationProps;
    deliveryDateText?: TypographyPresentationProps;
    shippingAddressGridItem?: GridItemProps;
    shippingAddress?: ShippingAddressInfoDisplayStyles;
    billingAddressGridItem?: GridItemProps;
    billingAddress?: BillingAddressInfoDisplayStyles;
    shippingInformationContainer?: GridItemProps;
    headingGridItem?: GridItemProps;
    headingText?: TypographyPresentationProps;
}

export const billingAndShippingAddressStyles: BillingAndShippingAddressStyles = {
    container: {
        gap: 20,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        > div {
                            padding: 4px 10px;
                        }
                    `,
                    css`
                        > div {
                            padding: 4px 10px;
                        }
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    carrierGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            flex-direction: column;
        `,
    },
    shippingServiceHeadingText: { weight: 600 },
    shippingServiceGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            flex-direction: column;
        `,
    },
    carrierHeadingText: { weight: 600 },
    deliveryDateGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            flex-direction: column;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        :empty {
                            display: none;
                        }
                    `,
                ], "max")}
        `,
    },
    deliveryDateHeadingText: { weight: 600 },
    shippingAddressGridItem: {
        width: [12, 12, 4, 4, 4],
    },
    billingAddressGridItem: {
        width: [12, 12, 4, 4, 4],
    },
    shippingInformationContainer: {
        width: [12, 12, 4, 4, 4],
        css: css`
            display: flex;
            flex-direction: column;
            padding-right: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-left: 10px;
                        padding-right: 10px;
                    `,
                    css`
                        padding-left: 10px;
                        padding-right: 10px;
                    `,
                    null,
                    null,
                    null,
                ])}
            > div {
                padding-right: 0;
                display: block;
                width: 100%;
                flex-basis: 100%;
                max-width: 100%;
                border: 1px solid transparent;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding-left: 0;
                        `,
                        css`
                            padding-left: 0;
                        `,
                        css``,
                        css`
                            padding-right: 0;
                        `,
                        css`
                            padding-right: 0;
                        `,
                    ])}
                > div {
                    display: flex;
                    flex-basis: 33.333333%;
                    max-width: 100%;
                    flex-wrap: wrap;
                    padding-top: 0;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                padding: 0;
                            `,
                            css`
                                padding: 0;
                            `,
                            null,
                            null,
                            null,
                        ])}
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                padding-left: 10px;
                                padding-right: 10px;
                            `,
                            css`
                                padding-left: 10px;
                                padding-right: 10px;
                            `,
                            null,
                            null,
                            null,
                        ])}
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                padding-left: 0;
                            `,
                            css`
                                padding-left: 0;
                            `,
                            css``,
                            css`
                                padding-right: 0;
                            `,
                            css`
                                padding-right: 0;
                            `,
                        ])}
                    > div {
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    padding: 0 10px;
                                `,
                                css`
                                    padding: 0 10px;
                                `,
                                null,
                                null,
                                null,
                            ])}
                    }
                    > div:first-of-type {
                        padding-bottom: 10px;
                        padding-top: 0;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    padding-bottom: 0;
                                `,
                                css`
                                    padding-bottom: 0;
                                `,
                                css``,
                                css`
                                    padding-right: 0;
                                `,
                                css`
                                    padding-right: 0;
                                `,
                            ])}
                    }
                }
            }
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            padding: 10px 10px 16px 10px;
            border-bottom: 1px solid ${getColor("common.border")};
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 6px 8px 8px;
                    `,
                    css`
                        padding: 6px 8px 8px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    headingText: {
        css: css`
            font-weight: 700;
            font-size: 16px;
            line-height: 24px;
        `,
    },
};

const BillingAndShippingInfo = ({
    billTo,
    shipTo,
    carrier,
    shipVia,
    deliveryDate,
    onEditBillTo,
    onEditShipTo,
    extendedStyles,
    cart,
    isUkWebsite,
}: Props) => {
    const styles = mergeToNew(billingAndShippingAddressStyles, extendedStyles);

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.shippingAddressGridItem}>
                <ShippingAddressInfoDisplay shipTo={shipTo} onEdit={onEditShipTo} extendedStyles={styles.shippingAddress} />
            </GridItem>
            {isUkWebsite || carrier?.description || shipVia?.description || deliveryDate ? (
                <GridItem {...styles.shippingInformationContainer}>
                    <GridItem>
                        <GridItem>
                            <GridItem {...styles.headingGridItem}>
                                <Typography {...styles.headingText}>Shipping Information</Typography>
                            </GridItem>
                            {isUkWebsite ? (
                                <GridItem {...styles.carrierGridItem}>
                                    <Typography {...styles.carrierText}>
                                        {siteMessage("CheckoutShipping_OrderConfirmation_ShipMethod_DisplayText")}
                                    </Typography>
                                </GridItem>
                            ) : carrier?.description ? (
                                <GridItem {...styles.carrierGridItem}>
                                    <Typography {...styles.carrierHeadingText}>{translate("Carrier")}</Typography>
                                    <Typography {...styles.carrierText}>{carrier?.description}</Typography>
                                </GridItem>
                            ) : null}
                            {!isUkWebsite && shipVia?.description ? (
                                <GridItem {...styles.shippingServiceGridItem}>
                                    <Typography {...styles.shippingServiceHeadingText}>{translate("Method")}</Typography>
                                    <Typography {...styles.shippingServiceText}>{shipVia?.description}</Typography>
                                </GridItem>
                            ) : null}

                            <GridItem {...styles.deliveryDateGridItem}>
                                {deliveryDate && (
                                    <>
                                        <Typography {...styles.deliveryDateHeadingText}>{translate("Requested Delivery Date")}</Typography>
                                        <Typography {...styles.deliveryDateText} data-test-selector="requestedDeliveryDateValue">
                                            <LocalizedDateTime dateTime={deliveryDate} />
                                        </Typography>
                                    </>
                                )}
                            </GridItem>
                        </GridItem>
                    </GridItem>
                </GridItem>
            ) : null}
            <GridItem {...styles.billingAddressGridItem}>
                <BillingAddressInfoDisplay billTo={billTo} onEdit={onEditBillTo} extendedStyles={styles.billingAddress} />
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps)(BillingAndShippingInfo);
